import {
  Box,
  Button,
  Chip,
  Divider,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Typography,
  useTheme,
} from '@mui/material'
import { DateTime } from 'luxon'
import React, { useEffect, useState } from 'react'
import { APPOINTMENT_CONFIG } from '../utils/constants'
import { client } from '../utils/client'
import { ApprenticeshipFormType } from '../utils/sharedTypes'
import Bugsnag from '@bugsnag/js'
import { MdNavigateNext } from 'react-icons/md'
import { BsCheck } from 'react-icons/bs'
import FormTitle from './FormTitle'

// Fuseau horaire de Paris (Europe/Paris)
const PARIS_TIMEZONE = 'Europe/Paris'

type AppointmentBookingProps = {
  form: ApprenticeshipFormType
  setForm: React.Dispatch<React.SetStateAction<ApprenticeshipFormType>>
}

type TimeSlot = {
  datetime: DateTime
  displayTime: string
  isAvailable: boolean
}

type DaySlots = {
  date: DateTime
  displayDate: string
  slots: TimeSlot[]
}

const AppointmentBooking: React.FC<AppointmentBookingProps> = ({ form, setForm }) => {
  const theme = useTheme()
  const [availableDays, setAvailableDays] = useState<DaySlots[]>([])
  const [selectedDate, setSelectedDate] = useState<DateTime | null>(null)
  const [selectedDateTime, setSelectedDateTime] = useState<string | null>(null)

  const submitInfo = () => {
    client
      .patch(`/apprenticeship/public/${form.data.global_id}/submit/`, {
        cs_appointment_datetime: selectedDateTime,
      })
      .then((resp) => {
        if (resp.status === 200) {
          if (selectedDateTime !== null) {
            setForm((prevState) => {
              return {
                ...prevState,
                data: {
                  ...prevState.data,
                  cs_appointment_datetime: selectedDateTime,
                },
              }
            })
          }
        }
      })
      .catch((err: Error) => {
        Bugsnag.notify(err)
        console.error(err)
      })
  }

  // Génère un identifiant unique pour les créneaux indisponibles basé sur la date
  const getUnavailableSlotsKey = (date: DateTime): string => {
    return `unavailable_slots_${date.toISODate()}`
  }

  // Récupère ou génère les créneaux indisponibles pour une date donnée
  const getUnavailableSlots = (date: DateTime, totalSlots: number): Set<number> => {
    const key = getUnavailableSlotsKey(date)
    const stored = localStorage.getItem(key)

    if (totalSlots < 6) {
      return new Set<number>()
    }

    if (stored) {
      try {
        return new Set(JSON.parse(stored))
      } catch {
        // Si erreur de parsing, on génère de nouveaux créneaux indisponibles
      }
    }

    // Génère 2 ou 3 créneaux indisponibles au hasard
    const unavailableCount = Math.random() < 0.5 ? 3 : 4
    const unavailableSlots = new Set<number>()

    while (unavailableSlots.size < unavailableCount && unavailableSlots.size < totalSlots) {
      const randomIndex = Math.floor(Math.random() * totalSlots)
      unavailableSlots.add(randomIndex)
    }

    // Stocke en localStorage
    localStorage.setItem(key, JSON.stringify(Array.from(unavailableSlots)))

    return unavailableSlots
  }

  // Génère les créneaux disponibles pour les 4 prochains jours ouvrés
  const generateAvailableSlots = (): DaySlots[] => {
    const days: DaySlots[] = []
    // Utilise le fuseau horaire de Paris pour la génération des créneaux
    const now = DateTime.now().setZone(PARIS_TIMEZONE)
    const minimumStartTime = now.plus({ hours: APPOINTMENT_CONFIG.MINIMUM_ADVANCE_HOURS })

    let currentDate = now.startOf('day')
    let workingDaysFound = 0

    // Trouve les 4 prochains jours ouvrés (en commençant par aujourd'hui si c'est un jour ouvré)
    while (workingDaysFound < APPOINTMENT_CONFIG.WORKING_DAYS_COUNT) {
      // Vérifie si c'est un jour ouvré (lundi à vendredi)
      if (currentDate.weekday >= 1 && currentDate.weekday <= 5) {
        const daySlots: TimeSlot[] = []

        // Génère les créneaux pour ce jour en fuseau horaire de Paris
        let currentTime = currentDate
          .set({
            hour: APPOINTMENT_CONFIG.START_HOUR,
            minute: APPOINTMENT_CONFIG.START_MINUTE,
            second: 0,
            millisecond: 0,
          })
          .setZone(PARIS_TIMEZONE)

        const endTime = currentDate
          .set({
            hour: APPOINTMENT_CONFIG.END_HOUR,
            minute: APPOINTMENT_CONFIG.END_MINUTE,
            second: 0,
            millisecond: 0,
          })
          .setZone(PARIS_TIMEZONE)

        // Génère d'abord tous les créneaux possibles
        const allSlots: TimeSlot[] = []
        while (currentTime < endTime) {
          const slotEndTime = currentTime.plus({ minutes: APPOINTMENT_CONFIG.SLOT_DURATION_MINUTES })

          // Vérifie si le créneau est disponible (après le délai minimum)
          const isAvailable = currentTime >= minimumStartTime && slotEndTime <= endTime

          allSlots.push({
            datetime: currentTime, // Garde le datetime en fuseau horaire de Paris
            displayTime: currentTime.toFormat('HH:mm'), // Affichage en fuseau horaire de Paris (sera converti pour l'affichage)
            isAvailable,
          })

          currentTime = currentTime.plus({ minutes: APPOINTMENT_CONFIG.SLOT_DURATION_MINUTES })
        }

        // Récupère les créneaux indisponibles aléatoires pour cette date
        const unavailableSlots = getUnavailableSlots(currentDate, allSlots.length)

        // Applique les créneaux indisponibles aléatoires
        allSlots.forEach((slot, index) => {
          if (unavailableSlots.has(index)) {
            slot.isAvailable = false
          }
        })

        daySlots.push(...allSlots)

        // Génère le texte d'affichage de la date
        const getDisplayDate = (date: DateTime): string => {
          const now = DateTime.now().setZone(PARIS_TIMEZONE)
          const today = now.startOf('day')
          const tomorrow = today.plus({ days: 1 })

          if (date.hasSame(today, 'day')) {
            return "Aujourd'hui"
          } else if (date.hasSame(tomorrow, 'day')) {
            return 'Demain'
          } else {
            return date.toFormat('cccc dd/MM/yyyy', { locale: 'fr' })
          }
        }

        // Ne pousse le jour que s'il y a au moins un créneau disponible
        const hasAvailableSlots = daySlots.some((slot) => slot.isAvailable)
        if (hasAvailableSlots) {
          days.push({
            date: currentDate,
            displayDate: getDisplayDate(currentDate),
            slots: daySlots,
          })

          workingDaysFound++
        }
      }

      // Passe au jour suivant
      currentDate = currentDate.plus({ days: 1 })
    }

    return days
  }

  useEffect(() => {
    setAvailableDays(generateAvailableSlots())
  }, [])

  // Met à jour la date sélectionnée quand un créneau est choisi
  useEffect(() => {
    if (selectedDateTime) {
      const selected = DateTime.fromISO(selectedDateTime).setZone(PARIS_TIMEZONE)
      setSelectedDate(selected.startOf('day'))
    }
  }, [selectedDateTime])

  const handleSlotSelect = (datetime: DateTime) => {
    // S'assure que le datetime est bien en fuseau horaire de Paris avant de le convertir en ISO
    const parisDateTime = datetime.setZone(PARIS_TIMEZONE)
    const isoString = parisDateTime.toISO()
    if (isoString) {
      setSelectedDateTime(isoString)
    }
  }

  const handleDateChange = (date: DateTime) => {
    // S'assure que la date est en fuseau horaire de Paris
    const parisDate = date.setZone(PARIS_TIMEZONE)
    setSelectedDate(parisDate)
    // Efface la sélection de créneau si on change de date
    if (selectedDateTime) {
      const currentSelected = DateTime.fromISO(selectedDateTime).setZone(PARIS_TIMEZONE)
      if (!currentSelected.hasSame(parisDate, 'day')) {
        setSelectedDateTime('')
      }
    }
  }

  const getSelectedDaySlots = (): DaySlots | null => {
    if (!selectedDate) return null
    return availableDays.find((day) => day.date.hasSame(selectedDate, 'day')) || null
  }

  const selectedDaySlots = getSelectedDaySlots()

  if (form.data.cs_appointment_datetime) {
    return (
      <Grid container justifyContent="center" alignItems="center" sx={{ minHeight: '100%', p: 2 }}>
        <Grid item xs={12} md={8} sx={{ mt: { xs: 15, md: 0 } }}>
          <Grid container justifyContent="center">
            <Grid
              item
              sx={{
                background: theme.palette.success.main,
                height: 80,
                width: 80,
                borderRadius: 40,
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
              }}
            >
              <BsCheck size="40px" style={{ color: 'white' }} />
            </Grid>
          </Grid>
          <FormTitle
            title={'Votre créneau a bien été réservé'}
            subtitle={`Votre créneau a bien été réservé le ${DateTime.fromISO(
              form.data.cs_appointment_datetime,
            ).toLocaleString(DateTime.DATE_MED)} entre ${DateTime.fromISO(
              form.data.cs_appointment_datetime,
            ).toLocaleString(DateTime.TIME_24_SIMPLE)} et ${DateTime.fromISO(form.data.cs_appointment_datetime)
              .plus({ minutes: APPOINTMENT_CONFIG.SLOT_DURATION_MINUTES })
              .toLocaleString(
                DateTime.TIME_24_SIMPLE,
              )}, l'un de nos responsables école vous appelleront pendant ce créneau dans la limite de leur disponibilité.`}
            align="center"
          />
        </Grid>
      </Grid>
    )
  }

  return (
    <Box>
      <Typography variant="body1" fontWeight="bold" gutterBottom>
        Prendre rendez-vous
      </Typography>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
        Sélectionnez un créneau sur lequel vous souhaitez être contacté par un responsable école pour valider votre
        dossier.
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
        Cet appel durera entre 10 et 15 minutes environ, c'est une vérification administrative de votre dossier pour
        savoir si nous pouvons vous présenter en jury d'admission par la suite.
      </Typography>

      {/* Sélection de la date */}
      <FormControl fullWidth sx={{ mb: 3, mt: 2 }}>
        <InputLabel>Choisir une date</InputLabel>
        <Select
          value={selectedDate?.toISODate() || ''}
          onChange={(e) => {
            if (e.target.value) {
              handleDateChange(DateTime.fromISO(e.target.value).setZone(PARIS_TIMEZONE))
            }
          }}
          label="Choisir une date"
        >
          {availableDays.map((day) => {
            const isoDate = day.date.toISODate()
            return (
              <MenuItem key={isoDate || ''} value={isoDate || ''} sx={{ textTransform: 'capitalize' }}>
                {day.displayDate}
              </MenuItem>
            )
          })}
        </Select>
      </FormControl>

      {/* Affichage des créneaux pour la date sélectionnée */}
      {selectedDaySlots && (
        <Box>
          <Typography variant="subtitle1" gutterBottom mb={2}>
            Créneaux disponibles{' '}
            {selectedDaySlots.displayDate === "Aujourd'hui" || selectedDaySlots.displayDate === 'Demain' ? '' : 'le '}
            {selectedDaySlots.displayDate.toLowerCase()} :
          </Typography>

          <Grid container spacing={1}>
            {selectedDaySlots.slots.map((slot, index) => {
              // Convertit l'heure d'affichage au fuseau horaire de l'utilisateur
              const userDisplayTime = slot.datetime.toFormat('HH:mm')

              return (
                <Grid item key={index}>
                  <Chip
                    label={userDisplayTime}
                    onClick={() => slot.isAvailable && handleSlotSelect(slot.datetime)}
                    disabled={!slot.isAvailable}
                    color={
                      selectedDateTime &&
                      DateTime.fromISO(selectedDateTime).setZone(PARIS_TIMEZONE).equals(slot.datetime)
                        ? 'primary'
                        : 'default'
                    }
                    variant={
                      selectedDateTime &&
                      DateTime.fromISO(selectedDateTime).setZone(PARIS_TIMEZONE).equals(slot.datetime)
                        ? 'filled'
                        : 'outlined'
                    }
                    sx={{
                      minWidth: 80,
                      cursor: slot.isAvailable ? 'pointer' : 'default',
                      opacity: slot.isAvailable ? 1 : 0.5,
                    }}
                  />
                </Grid>
              )
            })}
          </Grid>

          {selectedDateTime && (
            <Box sx={{ mt: 2 }}>
              <Divider sx={{ mb: 2 }} />

              <Typography variant="body2" color="primary" textAlign="justify">
                Vous avez sélectionné le créneau du{' '}
                {DateTime.fromISO(selectedDateTime).toFormat('cccc dd/MM/yyyy', { locale: 'fr' })} de{' '}
                {DateTime.fromISO(selectedDateTime).toFormat('HH:mm', { locale: 'fr' })} à{' '}
                {DateTime.fromISO(selectedDateTime)
                  .plus({ minutes: APPOINTMENT_CONFIG.SLOT_DURATION_MINUTES })
                  .toFormat('HH:mm', { locale: 'fr' })}
                , nos responsables vous appelleront pendant ce créneau dans la limite de leur disponibilité.
              </Typography>
              <Typography variant="body2" color="primary" textAlign="justify" mt={1}></Typography>
            </Box>
          )}
        </Box>
      )}

      {!selectedDate && (
        <Typography variant="body2" color="text.secondary" textAlign="center">
          Veuillez d'abord sélectionner une date pour voir les créneaux disponibles
        </Typography>
      )}

      {selectedDateTime && (
        <Grid
          item
          sx={{
            width: '100%',
            display: 'flex',
            justifyContent: 'flex-end',
            mt: 2,
          }}
        >
          <Button
            type="submit"
            variant="contained"
            sx={{
              height: {
                xs: 45,
              },
            }}
            disabled={!selectedDateTime}
            endIcon={<MdNavigateNext />}
            onClick={submitInfo}
          >
            Réserver mon créneau
          </Button>
        </Grid>
      )}
    </Box>
  )
}

export default AppointmentBooking
